import { Alert, Typography } from "@mui/material";
import type { VatStatus } from "Infrastructure/Api/Api";
import {
  isVatNumberRejectedByRegistry,
  isVatQuestionOpen,
} from "Schemas/vatNumberSchema";
import { Resources, useResource } from "Translations/Resources";

type Props = {
  vatRegistryStatus: VatStatus;
  vatNumber: string;
  // What to say when the register has not spoken (Unknown); null renders nothing for that case.
  openQuestionText: string | null;
};

/**
 * The register's word on a VAT ID, placed under its input: a warning for a number the register rejects,
 * the caller's text for an open question, otherwise the status line. Shared by the onboarding step and
 * the profile dialog so both say the same thing the same way.
 */
export const VatRegistryStatusText = ({
  vatRegistryStatus,
  vatNumber,
  openQuestionText,
}: Props) => {
  const { t } = useResource();

  if (isVatNumberRejectedByRegistry(vatRegistryStatus, vatNumber)) {
    return (
      <Alert severity="warning">
        {t(Resources.Common.VatNumberRejectedByRegistry)}
      </Alert>
    );
  }

  if (isVatQuestionOpen(vatRegistryStatus)) {
    if (openQuestionText === null) {
      return null;
    }

    return (
      <Typography variant="body2" color="text.secondary">
        {openQuestionText}
      </Typography>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      {t(Resources.Common.VatRegistryStatus, {
        status: t(Resources.Common.VatStatus[vatRegistryStatus]),
      })}
    </Typography>
  );
};
